import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { api } from './services/api';
import Login from './components/Login';
import Account from './pages/Account';
import TaskDetail from './pages/TaskDetail';
import Welcome from './pages/Welcome';
import ListSelection from './pages/ListSelection';
import MainView from './pages/MainView';
import { ListType } from './constants/listTypes';
import { useRefreshOnVisible } from './hooks/useRefreshOnVisible';
import { useLocationTracking } from './hooks/useLocationTracking';
import { useLocationPermissionCheck } from './hooks/useLocationPermissionCheck';
import { shouldShowList } from './utils/timeUtils';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const tomorrowCutoffHour = user?.tomorrow_cutoff_hour ?? 18;

  // Memoize visible lists for App component routes
  const appVisibleLists = useMemo(
    () => lists.filter(list => shouldShowList(list.list_type, tomorrowCutoffHour)),
    [lists, tomorrowCutoffHour]
  );

  // Initialize theme on mount
  useEffect(() => {
    // Check localStorage first for instant theme application
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      document.documentElement.setAttribute('data-theme', savedTheme);
    } else {
      // No saved preference, detect system preference
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
    }
  }, []);
  
  // Update theme when user data changes
  useEffect(() => {
    if (user && user.dark_mode_enabled !== undefined) {
      const theme = user.dark_mode_enabled ? 'dark' : 'light';
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('theme', theme);
    }
  }, [user]);

  // Keep the getLists priority-mode hint in sync with the user preference so the
  // backend can skip slot work when priority mode is off.
  useEffect(() => {
    if (user && user.priority_mode_enabled !== undefined) {
      api.setPriorityMode(!!user.priority_mode_enabled);
    }
  }, [user]);
  
  // Track location changes separately from list loading
  const locationTrackingEnabled = user?.location_aware_tasks_enabled || false;
  useLocationTracking(locationTrackingEnabled);

  // Check location permissions when app regains focus
  const {
    showPermissionPrompt,
    handleGoToPreferences,
    handleDismiss
  } = useLocationPermissionCheck(locationTrackingEnabled, isAuthenticated);

  const handleLogout = useCallback(() => {
    api.clearToken();
    setIsAuthenticated(false);
    setUser(null);
    setLists([]);
    setLoadError(false);
    navigate('/');
  }, [navigate]);

  const loadLists = useCallback(async () => {
    try {
      const data = await api.getLists();
      setLists(data);
      setLoadError(false);
    } catch (error) {
      console.error('Error loading lists:', error);
      if (error.message.includes('401') || error.message.includes('authenticate')) {
        handleLogout();
      } else {
        setLoadError(true);
      }
    } finally {
      setLoading(false);
    }
  }, [handleLogout]);


  // Store intended destination if user tries to access a list URL while logged out
  useEffect(() => {
    if (!isAuthenticated && location.pathname.startsWith('/list/')) {
      // Store the intended destination for redirect after login
      localStorage.setItem('redirect_after_login', location.pathname);
    }
  }, [location.pathname, isAuthenticated]);

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      setIsAuthenticated(true);
      loadLists();
      // Load user preferences so cutoff hour and other settings are available
      api.getUserPreferences().then(prefs => {
        setUser(prev => ({ ...prev, ...prefs }));
      }).catch(() => {});
    } else {
      setLoading(false);
    }
  }, [loadLists]);

  // URL parsing is handled in MainView via useParams, so we don't need duplicate logic here
  // This effect was causing feedback loops with the MainView effects

  // Refresh data when app regains focus (tab/window becomes visible)
  useRefreshOnVisible(loadLists, isAuthenticated);

  // Add class to body when banner is shown to adjust layout
  useEffect(() => {
    if (showPermissionPrompt) {
      document.body.classList.add('location-permission-banner-visible');
    } else {
      document.body.classList.remove('location-permission-banner-visible');
    }
    return () => {
      document.body.classList.remove('location-permission-banner-visible');
    };
  }, [showPermissionPrompt]);

  const handleLogin = async (userData) => {
    // This is called from Login component after Google authentication
    // The Login component already handled the API call and set the token
    // We just need to set the user state and mark as authenticated
    setUser(userData);
    setIsAuthenticated(true);
    setLoading(true); // Set loading to true before loading lists to prevent Welcome screen flash
    await loadLists();

    // Check if there's a stored redirect destination
    const redirectPath = localStorage.getItem('redirect_after_login');
    if (redirectPath) {
      localStorage.removeItem('redirect_after_login');
      // Navigate to the stored path now that lists are loaded
      navigate(redirectPath, { replace: true });
    }
  };

  const handleDeleteList = async (listId) => {
    await api.deleteList(listId);
    const updatedLists = lists.filter(list => list.id !== listId);
    setLists(updatedLists);

    // If currently viewing the deleted list, navigate to main list
    if (location.pathname === `/list/${listId}`) {
      const mainList = updatedLists.find(l => l.list_type === ListType.MAIN);
      if (mainList) {
        navigate(`/list/${mainList.id}`, { replace: true });
      } else {
        navigate('/', { replace: true });
      }
    }
  };

  if (!isAuthenticated) {
    return <Login onLogin={handleLogin} />;
  }

  if (loading) {
    return (
      <div className="App">
        <div className="loading-container">
          <div className="spinner"></div>
          <p className="loading-text">Loading your lists...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="App">
        <div className="loading-container">
          <p className="loading-text">Something went wrong on our end. Please try again shortly.</p>
          <button
            onClick={() => { setLoading(true); loadLists(); }}
            style={{
              padding: '0.5rem 1.5rem',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: 'var(--primary-color)',
              color: 'white',
              fontSize: '0.95rem',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check if user has no user-created lists (only main/tomorrow which are auto-created)
  // Only check this after loading is complete to avoid showing Welcome screen during load
  const userCreatedLists = lists.filter(list => 
    list.list_type === ListType.USER_LIST || list.list_type === ListType.DAILY_HABITS
  );
  const isFirstTimeUser = !loading && userCreatedLists.length === 0;

  return (
    <>
      {/* Location Permission Prompt Banner */}
      {showPermissionPrompt && (
        <div className="location-permission-banner">
          <div className="location-permission-banner-content">
            <div className="location-permission-banner-text">
              <strong>Location permission needed</strong>
              <span>Location-aware tasks are enabled, but location permission is not granted on this browser. Please grant permission to use this feature.</span>
            </div>
            <div className="location-permission-banner-actions">
              <button
                className="location-permission-banner-button location-permission-banner-button-primary"
                onClick={handleGoToPreferences}
              >
                Go to Preferences
              </button>
              <button
                className="location-permission-banner-button location-permission-banner-button-dismiss"
                onClick={handleDismiss}
                aria-label="Dismiss"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 4L4 12M4 4l8 8" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      <Routes>
        <Route
          path="/list/:listId"
          element={
            <MainView
              lists={lists}
              setLists={setLists}
              showCreateForm={showCreateForm}
              setShowCreateForm={setShowCreateForm}
              onDeleteList={handleDeleteList}
              onReloadLists={loadLists}
              onLogout={handleLogout}
              user={user}
            />
          }
        />
        <Route 
          path="/" 
          element={
            isFirstTimeUser ? (
              <Welcome 
                onCreateList={async (name) => {
                  const newList = await api.createList(name);
                  // Check if this list already exists in our lists (e.g., Daily Habits)
                  const existingList = lists.find(l => l.id === newList.id);
                  if (!existingList) {
                    setLists([...lists, newList]);
                  } else {
                    // List already exists, just refresh to get latest data
                    const data = await api.getLists();
                    setLists(data);
                  }
                  navigate(`/list/${newList.id}`);
                }}
              />
            ) : (
              <MainView
                lists={lists}
                setLists={setLists}
                showCreateForm={showCreateForm}
                setShowCreateForm={setShowCreateForm}
                onDeleteList={handleDeleteList}
                onReloadLists={loadLists}
                onLogout={handleLogout}
                user={user}
              />
            )
          } 
        />
        <Route
          path="/account/:section?"
          element={<Account user={user} onUserUpdate={setUser} />}
        />
        <Route 
          path="/task/:taskId" 
          element={
            <TaskDetail
              lists={lists}
              setLists={setLists}
              tomorrowCutoffHour={tomorrowCutoffHour}
              onTaskDataChange={async (taskId) => {
                const data = await api.getLists();
                setLists(data);
              }}
              onDeleteTask={async (listId, taskId) => {
                await api.deleteTask(taskId);
                const data = await api.getLists();
                setLists(data);
              }}
            />
          } 
        />
        <Route
          path="/lists"
          element={
            <ListSelection
              lists={appVisibleLists}
              onSelectList={(listId) => {
                navigate(`/list/${listId}`);
                setShowCreateForm(false);
              }}
              onCreateNew={() => setShowCreateForm(true)}
            />
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
